import React, { useState } from 'react';
import { Formik } from 'formik';
import _ from 'lodash';
import dayjs from 'dayjs';

// Components
import { FormikSubmitRegistrar, StageSubmitRegistrar } from '../../_core/Formik/formik-submit-registrar.component';
import { SessionStageBuilder } from './session-stage-builder.component';
import { ISessionStageBuilderFormValues } from './session-stage-builder.interface';
import { IFormOption } from '../../_core/_ui/forms.component';
import { ALL_REMINDER_NOTIFICATION_OPTIONS, buildOptions, EARLY_NOTIFICATION_OPTIONS, FINAL_NOTIFICATION_OPTIONS, WHEN_ASSIGNED_NOTIFICATION_OPTION } from '../assessment-stage.constants';
import { IAssessmentStageBuilderBaseProps } from '../assessment-stage.types';
import { getActiveCompanyId, getCompany } from '../../../lib/services/company.service';
import { getActiveAssessmentId, getAssessment } from '../../../lib/services/assessment.service';
import { CASCADING_SETTINGS } from '../../../lib/types/cascading-settings.types';
import { resolveCascadingSetting } from '../../../lib/helpers/cascading-settings.helpers';

const isValidTrackCount = (tracks: number | string) => Number.isInteger(Number(tracks)) && Number(tracks) >= 1;

interface SessionStageBuilderContainerProps extends IAssessmentStageBuilderBaseProps {
  stage: IAssessmentStage;
  updateStage: Function;
  // Only a stage that follows another session stage has a previous stage to overlap, so the
  // overlap setting renders and persists only then.
  followsSessionStage?: boolean;
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  // Catalog stage settings are shared across every association, so the editing admin's RSVP
  // default and a fixed absolute notification date stay out of the form and the payload.
  catalogSettingsMode?: boolean;
  registerStageSubmit?: StageSubmitRegistrar;
}

export const SessionStageBuilderContainer: React.FC<SessionStageBuilderContainerProps> = ({
  stage,
  updateStage,
  drillOptions,
  practicePlanOptions,
  catalogSettingsMode,
  followsSessionStage,
  registerStageSubmit,
  style
}) => {
  // A NEW stage's unset requestAttendance seeds from the assessment, then association, default.
  // See "Cascading-setting defaults resolve on `null` only — never on `false`" in CLAUDE.md.
  const { company } = getCompany(getActiveCompanyId());
  const { assessment } = getAssessment(getActiveAssessmentId());

  // Seeded once: a fresh Date per render reads as new initialValues to enableReinitialize,
  // which reset the form — and wiped unsaved picks — on every parent re-render.
  const [defaultNotificationDate] = useState(() => dayjs().add(1, 'day').toDate());
  const customNotificationDate = stage.config.customNotificationDate || defaultNotificationDate;

  const stagedNotifications: string[] = stage.config.notifications || [];
  const INITIAL_FORM_VALUES: ISessionStageBuilderFormValues = {
    drills: buildOptions(drillOptions, stage.config.drills || []),
    practicePlans: buildOptions(practicePlanOptions, stage.config.practicePlans || []),
    earlyReminder: EARLY_NOTIFICATION_OPTIONS.find(en => stagedNotifications.includes(en.value)),
    finalReminder: FINAL_NOTIFICATION_OPTIONS.find(en => stagedNotifications.includes(en.value)),
    enableNotifications: _.isBoolean(stage.config.enableNotifications) // Going forward this is considered enabling assignment notifications
      ? stage.config.enableNotifications && stagedNotifications.includes(WHEN_ASSIGNED_NOTIFICATION_OPTION.value)
      : true,
    enableReminders: _.isBoolean(stage.config.enableReminders) ? stage.config.enableReminders : stagedNotifications.some((n: string) => ALL_REMINDER_NOTIFICATION_OPTIONS.includes(n)),
    customNotificationDate,
    requestAttendance: _.isBoolean(stage.config.requestAttendance)
      ? stage.config.requestAttendance
      : !catalogSettingsMode && resolveCascadingSetting(CASCADING_SETTINGS.IS_ATTENDANCE_REQUESTED, { assessment, company }),
    allowOverlappingSessions: stage.config.allowOverlappingSessions === true,
    limitTracks: stage.config.tracks != null,
    tracks: stage.config.tracks ?? 1,
  };

  function validate({ limitTracks, tracks }: ISessionStageBuilderFormValues) {
    if (limitTracks && !isValidTrackCount(tracks)) return { tracks: 'Enter a whole number of at least 1.' };
    return {};
  }

  async function handleSubmit({ earlyReminder, finalReminder, requestAttendance, allowOverlappingSessions, customNotificationDate: customDate, limitTracks, tracks, ...values}: ISessionStageBuilderFormValues, { setSubmitting }: any) {
    setSubmitting(false);

    let notifications = [earlyReminder, finalReminder].filter(v => v) as IFormOption[];
    if (values.enableNotifications) notifications.push(WHEN_ASSIGNED_NOTIFICATION_OPTION);
    if (!values.enableReminders) {
      notifications = notifications.filter(n => n.value === "NOW");
    }

    const config = {
      ...values,
      drills: values.drills.map(drill => drill.value),
      practicePlans: values.practicePlans.map(practicePlan => practicePlan.value),
      notifications: notifications.map(notification => notification.value),
      // One check gates these fields' render and their payload — see "Render visibility and
      // data visibility are one decision" in CLAUDE.md.
      // null lifts the limit: the server merges config, so an omitted key would keep the old one.
      ...(!catalogSettingsMode && { requestAttendance, customNotificationDate: customDate, tracks: limitTracks ? Number(tracks) : null }),
      ...(followsSessionStage && { allowOverlappingSessions }),
    }

    updateStage(stage._id, { config });
  }

  return (
    <Formik enableReinitialize initialValues={INITIAL_FORM_VALUES} validate={validate} onSubmit={handleSubmit}>
      {form => (
        <>
          <FormikSubmitRegistrar id={stage._id} register={registerStageSubmit} />
          <SessionStageBuilder
            form={form}
            stage={stage}
            drillOptions={drillOptions}
            practicePlanOptions={practicePlanOptions}
            catalogSettingsMode={catalogSettingsMode}
            followsSessionStage={followsSessionStage}
            style={style}
          />
        </>
      )}
    </Formik>
  );
};

